import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import * as Icons from "@nova/component-icons"
import Image from "next/image"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { useMemo } from "react"
import { ButtonPrimary, ButtonSecondary, NovaButtonProps } from "@nova/component-btn"
import { selectIsDomainWhiteListed, selectUserAddOns } from "user/profile/selectors"
import { usePaywalls } from "features/entity-caching/licenses/useLicensePaywall"
import { usePluginState } from "../../usePluginState"
import { hideGlobalModal } from "runlevel/actions"

export const GenericTryPluginModal = ({
    pluginId,
    title,
    subtitle,
    body,
    footer,
    buttonTitle,
    onButtonClick,
    primaryButtonProps,
}: {
    pluginId: NovaPluginIds
    title: string
    subtitle: string
    body: string
    footer: string
    buttonTitle: string
    onButtonClick: () => void
    primaryButtonProps?: NovaButtonProps
}) => {
    const dispatch = useAppDispatch()

    const availableAddOns = useAppSelector(selectUserAddOns)
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const { showAddOnPaywallModal } = usePaywalls()

    const { pluginsThatSupportSampleData } = usePluginState()
    const plugin = useMemo(
        () => pluginsThatSupportSampleData.find((plugin) => plugin.featureId === pluginId),
        [pluginsThatSupportSampleData, pluginId]
    )

    if (!plugin)
        throw `Plugin with ID: ${pluginId} is missing the "sampleDataModalName" property wherever it was registered.`

    const userHasAccessToThisPlugin = plugin.subscriptionType
        ? availableAddOns[plugin.subscriptionType] || isWhitelisted
        : true

    return (
        <HighPriorityNovaModalContainer
            title="Back"
            closeModal={() => dispatch(hideGlobalModal())}
            className="w-[21.75rem]"
            onReturn={() =>
                dispatch(initializeCenterModalForComponentAction(ModalNames.TryOurAddOnsModal))
            }
            bottomBar={
                !userHasAccessToThisPlugin && plugin.subscriptionType ? (
                    <ButtonSecondary
                        title="Purchase Add-On"
                        LeftIcon={Icons.ShoppingCart}
                        className="w-full"
                        onClick={() => showAddOnPaywallModal(plugin.subscriptionType!)}
                    />
                ) : undefined
            }
        >
            <div className="mt-4 select-none">
                {plugin && (
                    <Image
                        src={plugin.iconUrl}
                        alt={plugin.displayName}
                        width={24}
                        height={24}
                        className="object-contain"
                        priority
                    />
                )}

                <h2 className="text-gray-0 mt-2.5 font-normal">{title}</h2>

                <h4 className="text-gray-0 mt-6">{subtitle}</h4>

                <h5 className="text-gray-300 mt-2">{body}</h5>

                <h5 className="text-gray-0 mt-6">{footer}</h5>

                <ButtonPrimary
                    title={buttonTitle}
                    RightIcon={Icons.ArrowRight}
                    className="mt-6"
                    onClick={onButtonClick}
                    {...primaryButtonProps}
                />
            </div>
        </HighPriorityNovaModalContainer>
    )
}
